import React, {useEffect, useState} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {extraLargeScreenL} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {FONT_SIZES} from '../../../constants/typography';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName, IconName} from '../../atoms/Icon/IconName';

export const SLIDE_IN_CONTAINER_POSITION = '3.4vw';

interface SlideInContainerProps {
  ['data-testid']?: string;
  className?: string;
  expanded?: boolean;
  onToggle?: (state: boolean) => void;
  headerProps: {
    text: string;
    iconName?: IconName;
    backgroundColor: keyof typeof COLORS;
  };
  showInfoIcon?: boolean;
  onInfoIconClick?: VoidFunction;
}

const HEADER_HEIGHT = 40;
const HEADER_X_PADDING = 16;

export const SlideInContainer: React.FC<
  React.PropsWithChildren<SlideInContainerProps>
> = ({
  'data-testid': dataTestId = 'slideInContainer',
  children,
  className,
  expanded,
  onToggle,
  headerProps,
  showInfoIcon = true,
  onInfoIconClick,
}) => {
  const [isOpen, setIsOpen] = useState(Boolean(expanded));

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setIsOpen(Boolean(expanded));
  }, [expanded]);

  const toggleState = () => {
    setIsOpen(isOpen => !isOpen);
    onToggle?.(!isOpen);
  };

  const handleInfoIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    isOpen && onInfoIconClick?.();
  };

  const guestimateHeaderWidth = () => {
    const textWidth = headerProps.text.length * 9.5;

    return (
      textWidth +
      HEADER_X_PADDING * 2 +
      (headerProps.iconName ? 20 : 0) +
      (showInfoIcon && isOpen ? 20 : 0)
    );
  };

  return (
    <Wrapper
      data-testid={dataTestId}
      className={className}
      data-open={isOpen}
      $maxWidth={guestimateHeaderWidth()}
    >
      <Header
        data-testid={`${dataTestId}Header`}
        $backgroundColor={headerProps.backgroundColor}
        onClick={toggleState}
      >
        <HeaderContent>
          {headerProps.iconName && (
            <Icon asset={headerProps.iconName} size="SS" fill="White" />
          )}
          <HeaderText>{headerProps.text}</HeaderText>
          <InfoIcon
            data-testid={`${dataTestId}InfoIcon`}
            data-is-shown={showInfoIcon && isOpen}
            onClick={handleInfoIconClick}
          >
            <Icon asset={GenericIconName.InfoFilled} size="XSS" fill="White" />
          </InfoIcon>
        </HeaderContent>
        <Icon
          asset={isOpen ? GenericIconName.ArrowDown : GenericIconName.ArrowUp}
          size="XS"
          fill="White"
        />
      </Header>
      {children}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $maxWidth: number;
}>`
  position: fixed;
  bottom: 0;
  right: ${SLIDE_IN_CONTAINER_POSITION};
  z-index: ${Z_INDEXES.SLIDE_IN_CONTAINER};
  overflow: hidden;

  will-change: max-width;
  max-width: ${props => props.$maxWidth}px;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: transform 250ms ease-in-out, max-width 250ms ease-in-out;
  transition-delay: 100ms;
  transform: translateY(calc(100% - ${HEADER_HEIGHT}px));

  &[data-open='true'] {
    transform: translateY(0);
    max-width: ${props => props.$maxWidth * 4}px;
  }

  ${extraLargeScreenL(css`
    transform: scale(1.2) translateY(calc(100% - ${HEADER_HEIGHT}px));
    right: 4.7vw;
    bottom: 16px;

    &[data-open='true'] {
      transform: scale(1.2) translateY(0);
    }
  `)}
`;

const Header = styled.div<{$backgroundColor: keyof typeof COLORS}>`
  display: flex;
  align-items: center;
  height: ${HEADER_HEIGHT}px;
  background-color: ${props => COLORS[props.$backgroundColor]};
  box-sizing: border-box;
  padding: 8px ${HEADER_X_PADDING}px;
  border-radius: 16px 16px 0px 0px;
  cursor: pointer;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const HeaderText = styled.span`
  position: relative;
  top: -1px;
  font-size: ${FONT_SIZES.px14};
  color: ${COLORS.White};
`;

const InfoIcon = styled.div`
  padding: 4px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  transition-delay: 250ms;
  opacity: 0;

  &[data-is-shown='true'] {
    opacity: 1;
  }
`;
